
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import {
  ClearRounded,
  EditRounded,
  VisibilityRounded,
} from "@mui/icons-material";

import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Divider,
} from "@mui/material";

import { connect } from "react-redux";

import { Alert, Dialog, Table } from "../../layout";
import AddEditCat from "./AddEditCat";

import * as actions from "../../api/actions/pos";
import { endpoints } from "../../api/actions/api";
import { AppContext } from "../../App";
import { safeOnComplete } from "../../utils/safeOnComplete";

import ReportPrintButton from "../reports/ReportPrintButton";

const Categories = (props) => {
  const { t } = useTranslation();

  const { _categoriesList, _get, _delete } = props;
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);
  const detailsRef = useRef(null);

  const [showForm, setShowForm] = useState(false);
  const [updData, setUpdData] = useState();

  const [showView, setShowView] = useState(false);
  const [viewData, setViewData] = useState(null);

  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });

  useEffect(() => {
    setLoader(true);

    _get(
      endpoints.Category,
      actions.ActionType.SetCategories,
      () => setLoader(false)
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddEdit = (val) => {
    setUpdData(val);
    setShowForm(true);
  };

  const handleView = (data) => {
    setViewData(data);
    setShowView(true);
  };

  const handleCloseView = () => {
    setShowView(false);
    setViewData(null);
  };

  const handleDlt = (id) => {
    setAlert({
      isOpen: true,
      type: "confirmation",
      title: t("areYouSure"),
      subTitle: t("deleteCategoryConfirmation"),

      onConfirm: () => {
        const onComplete = safeOnComplete(
          (res) => {
            setAlert({
              isOpen: true,
              type:
                res.status === 200
                  ? "success"
                  : "warning",
              title:
                res.status === 200
                  ? t("deletedSuccessfully")
                  : t("warning"),
              subTitle: res.msg,
            });
          },
          (err) => {
            setAlert({
              isOpen: true,
              type: "error",
              title: t("error"),
              subTitle: err.msg,
            });
          }
        );

        _delete(
          endpoints.Category,
          id,
          actions.ActionType.RmvCategory,
          onComplete
        );
      },
    });
  };

  const getParentCat = (catId) => {
    const cat = (_categoriesList || []).find(
      (item) => item.id === catId
    );

    return cat ? cat.name : "";
  };

  const tableData = useMemo(() => {
    const categories = _categoriesList || [];

    return categories.map((obj) => ({
      ...obj,
      parent:
        categories.find(
          (item) => item.id === obj.parentCatId
        )?.name || "",
    }));
  }, [_categoriesList]);

  const screenColumns = [
    {
      id: "actions",
      label: t("actions"),
    },
    {
      id: "name",
      label: t("name"),
    },
    {
      id: "descr",
      label: t("description"),
    },
    {
      id: "parent",
      label: t("parentCategory"),
    },
  ];

  const printColumns = screenColumns.filter(
    (column) => column.id !== "actions"
  );

  const ActionComp = (data) => (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {/* View */}
      <Tooltip arrow title={t("view")}>
        <IconButton
          onClick={() => handleView(data)}
          size="small"
          className="btn-primary"
        >
          <VisibilityRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Edit */}
      <Tooltip arrow title={t("editThisCategory")}>
        <IconButton
          onClick={() => handleAddEdit(data)}
          size="small"
          className="btn-primary"
        >
          <EditRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Delete */}
      <Tooltip arrow title={t("deleteThisCategory")}>
        <IconButton
          onClick={() => handleDlt(data.id)}
          size="small"
          className="btn-error"
        >
          <ClearRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const HeaderComp = () => (
    <Button
      onClick={() => handleAddEdit()}
      className="btn-secondary"
      sx={{
        width: { md: "180px", xs: "100%" },
        mb: { xs: "10px", md: "0px" },
        fontSize: "17px",
        padding: "8px",
      }}
    >
      {t("addCategory")}
    </Button>
  );

  const DetailRow = ({ label, value }) => (
    <Box sx={{ py: 1 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography
          variant="body1"
          sx={{ fontWeight: 600 }}
        >
          {label}:
        </Typography>

        <Typography
          variant="body1"
          sx={{
            textAlign: "right",
            overflowWrap: "anywhere",
          }}
        >
          {value || "-"}
        </Typography>
      </Box>

      <Divider sx={{ mt: 1 }} />
    </Box>
  );

  const CategoryDetails = () => (
    <Box>
      <Typography
        variant="h5"
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          mb: 3,
        }}
      >
        {t("categoryDetails", {
          defaultValue: "Category Details",
        })}
      </Typography>

      <DetailRow
        label={t("name")}
        value={viewData?.name}
      />

      <DetailRow
        label={t("description")}
        value={viewData?.descr}
      />

      <DetailRow
        label={t("parentCategory")}
        value={
          viewData
            ? getParentCat(viewData.parentCatId)
            : ""
        }
      />
    </Box>
  );

  return (
    <>
      {/* Categories heading and Print Preview */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("categories")}
        </Typography>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("categories")}
        />
      </Box>

      {/* Normal screen table */}
      <Table
        ActionComp={ActionComp}
        HeaderComp={HeaderComp}
        tblHead={screenColumns}
        tblData={tableData}
      />

      {/* Printable categories list */}
      <Box
        ref={reportRef}
        className="categories-report-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            mb: 2,
          }}
        >
          {t("categories")}
        </Typography>

        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={printColumns}
          tblData={tableData}
        />
      </Box>

      {/* Alerts */}
      <Alert alert={alert} setAlert={setAlert} />

      {/* Add / Edit dialog */}
      {showForm && (
        <Dialog
          show={showForm}
          onClose={false}
        >
          <AddEditCat
            updData={updData}
            setShowForm={setShowForm}
          />
        </Dialog>
      )}

      {/* View Category dialog */}
      {showView && viewData && (
        <Dialog
          show={showView}
          onClose={handleCloseView}
          maxWidth="sm"
        >
          <Box sx={{ p: 3 }}>
            {/* Print button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 2,
              }}
            >
              <ReportPrintButton
                reportRef={detailsRef}
                paperSize="Letter"
                reportTitle={t("categoryDetails", {
                  defaultValue: "Category Details",
                })}
              />
            </Box>

            {/* Screen details */}
            <Box
              sx={{
                backgroundColor: "#fff",
                color: "#000",
                p: 2,
              }}
            >
              <CategoryDetails />
            </Box>

            {/* Printable details */}
            <Box
              ref={detailsRef}
              className="category-details-print"
              sx={{
                position: "absolute",
                left: "-10000px",
                top: 0,
                width: "1000px",
                backgroundColor: "#fff",
                color: "#000",
                p: 2,
              }}
            >
              <CategoryDetails />
            </Box>

            {/* Close button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 3,
              }}
            >
              <Button
                variant="contained"
                onClick={handleCloseView}
              >
                {t("close")}
              </Button>
            </Box>
          </Box>
        </Dialog>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  _categoriesList: state.pos.categoriesList,
});

const mapActionToProps = {
  _get: actions.get,
  _delete: actions.Delete,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(Categories);
